import React, { useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon, AttachmentIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import useDebounce from 'constants/helper/useDebounce';
import { useBookList } from './hooks/useBookList';
import { useQueryClient } from '@tanstack/react-query';
import {
  useBookDelete,
  useGeneralInformationDelete,
} from './hooks/useBookDelete';
import Swal from 'sweetalert2';
import BookForm from './bookForm';

const ElectropathyBook = () => {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);
  const queryClient = useQueryClient();

  const handleSearch = (value) => setSearchString(value);
  const { books, totalPages, isLoading } = useBookList(page, debouncedSearch);

  const tableData = books?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const { mutate: deleteBook } = useBookDelete();
  const swalBackground = useColorModeValue('#ffffff', '#1A202C');
  const swalColor = useColorModeValue('#000000', '#ffffff');

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This Book will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#319795',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      background: swalBackground,
      color: swalColor,
      borderRadius: '15px',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBook(id, {
          onSuccess: () => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Book has been deleted successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false,
              background: swalBackground,
              color: swalColor,
            });

            queryClient.invalidateQueries(['bookList']);
          },
        });
      }
    });
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            {
              key: 'name',
              label: 'Name',
              width: '500px',
              headerSize: '14px',
            },
            {
              key: 'pdf',
              label: 'Attachment',
              width: '500px',
              headerSize: '14px',
            },
          ]}
          page={page}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={setPage}
          showTopAction={true}
          topAction={{
            title: 'Electropathy Book',
            label: 'Add Book',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showSearch={true}
          onSearch={handleSearch}
          showAddBtn={true}
          showViewToggle={false}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onDelete={(row) => handleDelete(row._id)}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              Electropathy Book
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <BookForm
            setSelectedId={setSelectedId}
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['bookList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ElectropathyBook;
